<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const { t } = useI18n()
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main id="main-content" class="min-h-screen">
      <section id="page-header" class="bg-white border-b border-gray-200 py-6">
        <div class="max-w-7xl mx-auto px-5">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-3xl font-bold text-accent mb-2">
                {{ $t('developer.request_credits.title') }}
              </h1>
              <p class="text-gray-600">
                {{ $t('developer.request_credits.subtitle') }}
              </p>
            </div>
            <button
              class="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 flex items-center space-x-2"
            >
              <i class="fas fa-plus"></i>
              <span>{{ $t('developer.request_credits.new_issuance_request') }}</span>
            </button>
          </div>
        </div>
      </section>

      <div class="max-w-7xl mx-auto px-5 py-8">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div id="issuance-form" class="lg:col-span-2">
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <div class="flex items-center space-x-3 mb-6">
                <div
                  class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-certificate text-primary"></i>
                </div>
                <div>
                  <h2 class="text-xl font-semibold text-accent">
                    {{ $t('developer.request_credits.credit_issuance_request.title') }}
                  </h2>
                  <p class="text-sm text-gray-600">
                    {{ $t('developer.request_credits.credit_issuance_request.subtitle') }}
                  </p>
                </div>
              </div>

              <form class="space-y-6">
                <div
                  id="project-selection"
                  class="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div>
                    <label class="block text-sm font-medium text-accent mb-2"
                      >{{ $t('developer.request_credits.credit_issuance_request.project_name') }}</label
                    >
                    <select
                      class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option>{{ $t('developer.request_credits.credit_issuance_request.select_project') }}</option>
                      <option>Amazon Reforestation Initiative - CR001</option>
                      <option>Solar Energy Project Kenya - CR002</option>
                      <option>Mangrove Restoration Program - CR003</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-accent mb-2"
                      >{{ $t('developer.request_credits.credit_issuance_request.certificate_registration') }}</label
                    >
                    <input
                      type="text"
                      value="CR001-2024-001"
                      readonly
                      class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>

                <div
                  id="monitoring-period"
                  class="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div>
                    <label class="block text-sm font-medium text-accent mb-2"
                      >{{ $t('developer.request_credits.credit_issuance_request.monitoring_period_start') }}</label
                    >
                    <input
                      type="date"
                      class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-accent mb-2"
                      >{{ $t('developer.request_credits.credit_issuance_request.monitoring_period_end') }}</label
                    >
                    <input
                      type="date"
                      class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div id="credit-details">
                  <label class="block text-sm font-medium text-accent mb-2"
                    >{{ $t('developer.request_credits.credit_issuance_request.requested_credits') }}</label
                  >
                  <input
                    type="number"
                    :placeholder="$t('developer.request_credits.credit_issuance_request.credits_placeholder')"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div id="document-uploads" class="space-y-4">
                  <h3 class="text-lg font-semibold text-accent">
                    {{ $t('developer.request_credits.required_documents.title') }}
                  </h3>

                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-4"
                  >
                    <div class="text-center">
                      <i
                        class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                      ></i>
                      <p class="text-sm font-medium text-accent">
                        {{ $t('developer.request_credits.required_documents.doe_verification') }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ $t('developer.request_credits.required_documents.upload_pdf') }}
                      </p>
                      <button
                        type="button"
                        class="mt-2 bg-primary text-white px-4 py-2 rounded-md text-sm"
                      >
                        {{ $t('developer.request_credits.required_documents.choose_file') }}
                      </button>
                    </div>
                  </div>

                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-4"
                  >
                    <div class="text-center">
                      <i
                        class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                      ></i>
                      <p class="text-sm font-medium text-accent">
                        {{ $t('developer.request_credits.required_documents.monitoring_reports') }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ $t('developer.request_credits.required_documents.upload_multiple') }}
                      </p>
                      <button
                        type="button"
                        class="mt-2 bg-primary text-white px-4 py-2 rounded-md text-sm"
                      >
                        {{ $t('developer.request_credits.required_documents.choose_files') }}
                      </button>
                    </div>
                  </div>

                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-4"
                  >
                    <div class="text-center">
                      <i
                        class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                      ></i>
                      <p class="text-sm font-medium text-accent">
                        {{ $t('developer.request_credits.required_documents.ics_certificates') }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ $t('developer.request_credits.required_documents.legacy_projects_only') }}
                      </p>
                      <button
                        type="button"
                        class="mt-2 bg-gray-400 text-white px-4 py-2 rounded-md text-sm"
                      >
                        {{ $t('developer.request_credits.required_documents.choose_file') }}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div id="issuance-summary" class="space-y-6">
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <h3 class="text-lg font-semibold text-accent mb-4">
                {{ $t('developer.request_credits.issuance_summary.title') }}
              </h3>

              <div class="space-y-4">
                <div
                  class="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <span class="text-sm text-gray-600">{{ $t('developer.request_credits.issuance_summary.requested_credits') }}</span>
                  <span class="font-medium text-accent">10,000</span>
                </div>

                <div
                  class="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                >
                  <h4 class="font-medium text-yellow-800 mb-2">
                    {{ $t('developer.request_credits.issuance_summary.mandatory_deductions.title') }}
                  </h4>
                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <span class="text-yellow-700">{{ $t('developer.request_credits.issuance_summary.mandatory_deductions.ndc_retirement') }}</span>
                      <span class="font-medium">100 credits</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-yellow-700">{{ $t('developer.request_credits.issuance_summary.mandatory_deductions.buffer_account') }}</span>
                      <span class="font-medium">200 credits</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-yellow-700"
                        >{{ $t('developer.request_credits.issuance_summary.mandatory_deductions.transaction_account') }}</span
                      >
                      <span class="font-medium">3,000 credits</span>
                    </div>
                  </div>
                </div>

                <div
                  class="flex justify-between items-center py-2 border-t border-gray-200"
                >
                  <span class="font-medium text-accent"
                    >{{ $t('developer.request_credits.issuance_summary.net_credits') }}</span
                  >
                  <span class="text-xl font-bold text-primary">6,700</span>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <h3 class="text-lg font-semibold text-accent mb-4">
                {{ $t('developer.request_credits.fee_calculation.title') }}
              </h3>

              <div class="space-y-3">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">{{ $t('developer.request_credits.fee_calculation.cci_certificate_fee') }}</span>
                  <span class="font-medium">$200.00</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">{{ $t('developer.request_credits.fee_calculation.credit_issuance_fee') }}</span>
                  <span class="text-xs text-gray-500"
                    >($0.10 × 10,000 credits)</span
                  >
                </div>
                <div class="flex justify-between items-center">
                  <span class="font-medium"></span>
                  <span class="font-medium">$1,000.00</span>
                </div>
                <div class="border-t border-gray-200 pt-3">
                  <div class="flex justify-between items-center">
                    <span class="font-semibold text-accent">{{ $t('developer.request_credits.fee_calculation.total_fee') }}</span>
                    <span class="text-xl font-bold text-primary"
                      >$1,200.00</span
                    >
                  </div>
                </div>
              </div>

              <button
                class="w-full bg-secondary text-accent py-3 rounded-lg font-medium mt-4 hover:bg-secondary/90"
              >
                {{ $t('developer.request_credits.fee_calculation.proceed_payment') }}
              </button>
            </div>

            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-start space-x-3">
                <i class="fas fa-info-circle text-blue-600 mt-1"></i>
                <div>
                  <h4 class="font-medium text-blue-800 mb-1">
                    {{ $t('developer.request_credits.processing_timeline.title') }}
                  </h4>
                  <p class="text-sm text-blue-700">
                    {{ $t('developer.request_credits.processing_timeline.message') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="recent-issuances" class="mt-12">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-accent">
                {{ $t('developer.request_credits.recent_issuances.title') }}
              </h2>
              <div class="flex items-center space-x-3">
                <select
                  class="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option>{{ $t('developer.request_credits.recent_issuances.status.all') }}</option>
                  <option>{{ $t('developer.request_credits.recent_issuances.status.pending') }}</option>
                  <option>{{ $t('developer.request_credits.recent_issuances.status.processing') }}</option>
                  <option>{{ $t('developer.request_credits.recent_issuances.status.completed') }}</option>
                  <option>{{ $t('developer.request_credits.recent_issuances.status.rejected') }}</option>
                </select>
                <button class="text-primary hover:text-primary/80">
                  <i class="fas fa-filter"></i>
                </button>
              </div>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.request_id') }}
                    </th>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.project') }}
                    </th>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.credits') }}
                    </th>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.fee') }}
                    </th>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.status') }}
                    </th>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.date') }}
                    </th>
                    <th
                      class="text-left py-3 px-4 font-medium text-accent text-sm"
                    >
                      {{ $t('developer.request_credits.recent_issuances.columns.actions') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr>
                    <td class="py-3 px-4 text-sm font-medium text-accent">
                      ISS-2024-001
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">
                      Amazon Reforestation
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">10,000</td>
                    <td class="py-3 px-4 text-sm text-gray-600">$1,200.00</td>
                    <td class="py-3 px-4">
                      <span
                        class="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium"
                        >{{ $t('developer.request_credits.recent_issuances.status.completed') }}</span
                      >
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">
                      Dec 15, 2024
                    </td>
                    <td class="py-3 px-4">
                      <button
                        class="text-primary hover:text-primary/80 text-sm"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td class="py-3 px-4 text-sm font-medium text-accent">
                      ISS-2024-002
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">
                      Solar Energy Kenya
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">7,500</td>
                    <td class="py-3 px-4 text-sm text-gray-600">$950.00</td>
                    <td class="py-3 px-4">
                      <span
                        class="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
                        >{{ $t('developer.request_credits.recent_issuances.status.processing') }}</span
                      >
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">
                      Dec 12, 2024
                    </td>
                    <td class="py-3 px-4">
                      <button
                        class="text-primary hover:text-primary/80 text-sm"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td class="py-3 px-4 text-sm font-medium text-accent">
                      ISS-2024-003
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">
                      Mangrove Restoration
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">5,200</td>
                    <td class="py-3 px-4 text-sm text-gray-600">$720.00</td>
                    <td class="py-3 px-4">
                      <span
                        class="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium"
                        >{{ $t('developer.request_credits.recent_issuances.status.pending') }}</span
                      >
                    </td>
                    <td class="py-3 px-4 text-sm text-gray-600">
                      Dec 10, 2024
                    </td>
                    <td class="py-3 px-4">
                      <button
                        class="text-primary hover:text-primary/80 text-sm"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
